import {
  BehaviorSubject,
  Observable,
  firstValueFrom,
  lastValueFrom,
} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { AuthLoginDto, AuthLoginResponseDto, AuthPasswordResetDto, AuthRegisterDto } from '@common/dtos';
import { ProtoUrl } from 'src/app/@shared/protoUrl';
import { StorageService } from 'src/app/@core/storage.service';
import { User } from '@common/entities';
import { UserModel } from 'src/app/user/@shared/user.model';
import { UserService } from 'src/app/user/@shared/user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private isAuthenticated$ = new BehaviorSubject<boolean>(false);

  constructor(private http: HttpClient,
    private storage: StorageService,
    private userService: UserService) {
    this.isAuthenticated$.next(this.isAuthenticated());

    window.addEventListener('storage', (event: StorageEvent) => {
      if (!event.key || !event.key.includes('accessToken')) {
        return;
      }

      this.isAuthenticated$.next(this.isAuthenticated());

      if (!this.isAuthenticated()) {
        this.userService.removeCurrentUser();
      }
    });
  }

  public getAuthState(): Observable<boolean> {
    return this.isAuthenticated$.asObservable();
  }

  public getAccessToken(): string {
    return this.storage.get('accessToken', null) as string;
  }

  public isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  public async register(registerData: AuthRegisterDto): Promise<UserModel> {
    try {
      await this.http.post(ProtoUrl.authRegister(), registerData).toPromise();

      return this.login(registerData);
    } catch (error) {
      console.error(error);

      return Promise.reject(error);
    }
  }

  public async login(loginData: AuthLoginDto): Promise<UserModel> {
    try {
      const loginResponse = await this.http.post<AuthLoginResponseDto>(ProtoUrl.authLogin(), loginData).toPromise();

      this.storage.set('accessToken', loginResponse.accessToken);
      this.userService.updateCurrentUser(loginResponse.user as User).catch();

      this.isAuthenticated$.next(true);

      return new UserModel(loginResponse.user);
    } catch (error) {
      console.error(error);

      return Promise.reject(error);
    }
  }

  public async logout(preventAuthRequest?: boolean): Promise<void> {
    if (!preventAuthRequest) {
      try {
        await lastValueFrom(this.http.post<void>(ProtoUrl.authLogout(), {}));
      } catch (error) {
        // Catch server side logout error and continue with client side logout
        console.error(error);
      }
    }

    this.removeCriticalData();

    return Promise.resolve();
  }

  public requestPasswordReset(email: string): Promise<void> {
    const stream = this.http.post<void>(ProtoUrl.authRequestPasswordReset(), {
      email,
    });

    return firstValueFrom(stream);
  }

  public resetPassword(data: AuthPasswordResetDto): Promise<void> {
    return this.http.post<void>(ProtoUrl.authResetPassword(), data)
      .toPromise();
  }

  public removeCriticalData(): void {
    this.userService.removeCurrentUser();
    this.storage.remove('accessToken');
    this.isAuthenticated$.next(false);
  }
}
